import type { CSSProperties } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TASK_STEP_ATTR, TASK_STEP_KEYS, type TaskStep, type TaskSteps } from './task-steps';

export type { TaskStep, TaskSteps };

function hint(step: TaskStep, which: 'previous' | 'next'): string {
  const name = which === 'previous' ? 'Previous' : 'Next';
  return step ? `${name}: ${step.label} (${TASK_STEP_KEYS[which]})` : which === 'previous' ? 'This is the first one' : 'This is the last one';
}

/**
 * Desktop: one round arrow each side of the pop-up, in the margin beside it (TaskPopup's `steps`). Phones have none
 * (SF-T-274: they crowded the task's top bar off the screen); there you close the task and pick the next. At either
 * end of the list its arrow is greyed out rather than gone, so the other stays put.
 * `fixed` puts them at the window's edges (off: the gallery's box); `rightInset` moves the right one in, left of the docked chat.
 */
export function TaskStepArrows({ steps, fixed = true, rightInset, className }: { steps: TaskSteps; fixed?: boolean; rightInset?: string; className?: string }) {
  // A greyed-out arrow still takes the click, so it doesn't fall through to the overlay and close the task.
  const side = cn('pointer-events-auto top-1/2 disabled:pointer-events-auto disabled:cursor-default z-50 -translate-y-1/2 rounded-full bg-background shadow-md', fixed ? 'fixed' : 'absolute');
  const right: CSSProperties | undefined = rightInset ? { right: `calc(${rightInset} + 0.75rem)` } : undefined;
  return (
    <div className={className} {...{ [TASK_STEP_ATTR]: '' }}>
      <Button
        variant="outline"
        size="icon-lg"
        className={cn(side, 'left-3')}
        aria-label="Previous task"
        data-hint={hint(steps.previous, 'previous')}
        disabled={!steps.previous}
        onClick={steps.previous?.onSelect}
      >
        <ChevronLeft className="size-5" />
      </Button>
      <Button
        variant="outline"
        size="icon-lg"
        className={cn(side, 'right-3')}
        style={right}
        aria-label="Next task"
        data-hint={hint(steps.next, 'next')}
        disabled={!steps.next}
        onClick={steps.next?.onSelect}
      >
        <ChevronRight className="size-5" />
      </Button>
    </div>
  );
}
